import type { IReviewProviderScoreData } from "@krea/shared/types/common";

import { GoogleLogo } from "./assets/google-logo";
import { GoogleStars } from "./assets/google-stars";
import { TrustpilotLogo } from "./assets/trustpilot-logo";
import { TrustpilotStars } from "./assets/trustpilot-stars";
import { InformationBox } from "./information-box";
import { ReviewProviderBadge } from "./review-provider-badge";
import { ReviewProviderCard } from "./review-provider-card";

export interface IReviewProviderDisplayProps {
  data: IReviewProviderScoreData;
  provider: "trustpilot" | "google_reviews";
  mode: "badge" | "card";
  gradeLabel?: string;
  reviewCountText?: string;
  className?: string;
  showReviewCount?: boolean;
  variant?: "default" | "compact";
  wrapInInformationBox?: boolean;
  cardLayout?: "stacked" | "inline";
}

export const ReviewProviderDisplay = ({
  data,
  provider,
  mode,
  gradeLabel,
  reviewCountText,
  className,
  showReviewCount,
  variant,
  wrapInInformationBox,
  cardLayout = "stacked",
}: IReviewProviderDisplayProps) => {
  const isTrustpilot = provider === "trustpilot";
  // Brand names — not translated.
  const providerName = isTrustpilot ? "Trustpilot" : "Google";

  if (mode === "card") {
    const card = isTrustpilot ? (
      <ReviewProviderCard
        data={data}
        providerName={providerName}
        className={className}
        gradeLabel={gradeLabel}
        layout={cardLayout}
        reviewCountText={reviewCountText}
        logo={
          <TrustpilotLogo
            decorative
            className={cardLayout === "inline" ? "h-5 w-auto" : "h-5"}
          />
        }
        stars={
          <TrustpilotStars
            className={cardLayout === "inline" ? "h-4" : "mt-1 h-4"}
            score={data.score}
          />
        }
      />
    ) : (
      <ReviewProviderCard
        data={data}
        providerName={providerName}
        className={className}
        layout={cardLayout}
        reviewCountText={reviewCountText}
        logo={
          <GoogleLogo
            decorative
            className={cardLayout === "inline" ? "h-6 w-auto min-w-0" : "h-6"}
          />
        }
        stars={
          <GoogleStars
            className={cardLayout === "inline" ? "h-4" : "h-4"}
            score={data.score}
          />
        }
      />
    );

    if (wrapInInformationBox) {
      return (
        <InformationBox link={data.linkToProviderReviews} className={className}>
          {card}
        </InformationBox>
      );
    }

    return card;
  }

  if (isTrustpilot) {
    return (
      <ReviewProviderBadge
        data={data}
        providerName={providerName}
        className={className}
        variant={variant}
        gradeLabel={gradeLabel}
        showReviewCount={showReviewCount}
        reviewCountText={reviewCountText}
        logo={
          <TrustpilotLogo
            decorative
            className="flex self-start h-5 min-h-5 w-auto shrink-0 overflow-visible"
          />
        }
      />
    );
  }

  return (
    <ReviewProviderBadge
      data={data}
      providerName={providerName}
      className={className}
      variant={variant}
      showReviewCount={showReviewCount}
      reviewCountText={reviewCountText}
      starsOrGrade={<GoogleStars className="h-4" score={data.score} />}
      logo={<GoogleLogo decorative className="h-5" />}
    />
  );
};
